// Gestão de Pedidos (épico PO1-2374, sprint 612): chamadas da tela Trackfy ›
// Operação › Pedidos. Instância `api` (backend_hub, JWT + X-Active-Embarcador-Id).
// O número do pedido vai SEMPRE no body ou na query (`pedido`), nunca no path:
// pode conter "/".
//
//   GET    /trackfy/pedidos                 lista agrupada por pedido (+ marcadores, etiquetas_*)
//   GET    /trackfy/pedidos/notas?pedido=   notas do pedido (linha aberta)
//   GET    /pedidos/detalhe?pedido=         { pedido|null, marcadores[], historico[] }
//   PUT    /pedidos/operacional             { pedido, ...campos } → { pedido, alterados[] }
//   GET    /pedidos/marcadores?busca=       [{ marcador, pedidos }]
//   POST   /pedidos/marcadores/lote         { pedidos[], adicionar[], remover[] }
//   GET    /pedidos/comprovantes?pedido=    { ocorrencias[], manuais[], storageDisponivel }
//   POST   /pedidos/comprovantes            multipart `arquivos` + pedido + idNotaFiscal?
//   DELETE /pedidos/comprovantes/:id
//   GET    /pedidos/transportadoras         [{ id, cnpj, razaoSocial, fantasia }]
//   POST   /pedidos/transportadora          { pedido, idTransportadora, motivo }
//   POST   /pedidos/notas                   { pedidos[] } → notas dos pedidos
//   POST   /pedidos/notfis                  { pedidos[] } → text/plain (download)
//   POST   /pedidos/etiquetas/arquivo       { pedidos[], formato } → blob (+ X-Etiquetas-Ignoradas)
//   POST   /despacho/lotes/adicionar        { pedidos[] }
//   GET/POST /notas-fiscais/consulta/:id/etiqueta  (PO1-3378) opções e geração por NF
//
// Node 12 no build: sem optional chaining nem nullish coalescing.
import api from "./api";

const lista = (data) => (Array.isArray(data) ? data : []);

// Permissões do módulo (hub.operacao.*). O menu da tela continua em Pages.HUB.Pedidos.
// Etiquetas (gerar, re-tentar, imprimir) não têm permissão própria: quem vê a
// tela de Pedidos gera etiquetas (decisão do QA de 12/10).
export const PERMISSOES = {
  VER: "hub.operacao.pedidos.visualizar",
  EDITAR: "hub.operacao.pedidos.editar",
  TRANSPORTADORA: "hub.operacao.pedidos.transportadora",
  NOTFIS: "hub.operacao.notfis.gerar",
  DESPACHO: "hub.operacao.despacho.editar",
  ROMANEIO: "hub.romaneio.manual.editar",
};

// Limite do backend para as ações em massa (NOTFIS, etiquetas, lote, marcadores).
export const MAX_PEDIDOS_LOTE = 500;
// Limite do backend para o filtro `marcadores` da listagem.
export const MAX_MARCADORES_FILTRO = 20;

// Mensagem quando a seleção passa do limite das ações em massa; "" quando cabe.
export function excedeLote(quantidade) {
  const n = Number(quantidade) || 0;
  if (n <= MAX_PEDIDOS_LOTE) return "";
  return `Selecione até ${MAX_PEDIDOS_LOTE} pedidos por ação em massa (há ${n} selecionados). Desmarque alguns ou faça em partes.`;
}

// ─── Listagem (antes inline em Pedido/index.js) ─────────────────────────────
export async function listarPedidos(params) {
  const { data } = await api.get("/trackfy/pedidos", { params });
  return data || { itens: [], total: 0, resumo: {}, notasSemPedido: 0 };
}

export async function listarNotasDoPedido(params) {
  const { data } = await api.get("/trackfy/pedidos/notas", { params });
  return lista(data);
}

// ─── Detalhe e dados operacionais (H2) ──────────────────────────────────────
// 404 quando o pedido não existe no embarcador.
export async function obterDetalhe(pedido) {
  const { data } = await api.get("/pedidos/detalhe", { params: { pedido } });
  return data || { pedido: null, marcadores: [], historico: [] };
}

export const CAMPOS_OPERACIONAIS = [
  "codigoRastreio",
  "qtdVolumes",
  "peso",
  "altura",
  "largura",
  "comprimento",
  "contatoNome",
  "contatoTelefone",
  "contatoEmail",
  "observacao",
];

// { pedido, alterados[] }; 400 { error, errors: { campo: msg } }.
export async function atualizarOperacional(pedido, campos) {
  const corpo = { pedido };
  CAMPOS_OPERACIONAIS.forEach((k) => {
    if (campos && campos[k] !== undefined) corpo[k] = campos[k];
  });
  const { data } = await api.put("/pedidos/operacional", corpo);
  return data || { pedido: null, alterados: [] };
}

// ─── Marcadores (H2) ────────────────────────────────────────────────────────
// [{ marcador, pedidos }] do embarcador (autocompletar e filtro).
export async function listarMarcadores(busca) {
  const params = busca ? { busca } : {};
  const { data } = await api.get("/pedidos/marcadores", { params });
  return lista(data);
}

// { atualizados, naoEncontrados[], adicionados, removidos }.
export async function aplicarMarcadores({ pedidos, adicionar, remover }) {
  const { data } = await api.post("/pedidos/marcadores/lote", {
    pedidos: lista(pedidos),
    adicionar: lista(adicionar),
    remover: lista(remover),
  });
  return data || { atualizados: 0, naoEncontrados: [], adicionados: 0, removidos: 0 };
}

// "a, b" (string da listagem) → ["a", "b"].
export function marcadoresDaLinha(valor) {
  if (Array.isArray(valor)) return valor.filter(Boolean);
  if (!valor) return [];
  return String(valor)
    .split(",")
    .map((m) => m.trim())
    .filter(Boolean);
}

// ─── Comprovantes (H6) ──────────────────────────────────────────────────────
export const COMPROVANTE_EXTENSOES = ["pdf", "jpg", "jpeg", "png"];
export const COMPROVANTE_ACCEPT = COMPROVANTE_EXTENSOES.map((e) => "." + e).join(",");
export const COMPROVANTE_MAX_ARQUIVOS = 5;
export const COMPROVANTE_MAX_BYTES = 10 * 1024 * 1024;
export const COMPROVANTE_LEGENDA = "PDF, JPG ou PNG · até 5 por envio · 10 MB cada";

export function extensaoDe(nome) {
  const partes = String(nome || "").split(".");
  if (partes.length < 2) return "";
  const ultimo = String(partes[partes.length - 1] || "");
  return /^[A-Za-z0-9]{1,10}$/.test(ultimo) ? ultimo.toLowerCase() : "";
}

// Mesmas regras do backend (PedidoService.enviarComprovantes), antes do request.
// `jaEscolhidos` = quantos arquivos válidos a tela já tem: os novos só entram
// até completar o limite; os excedentes são rejeitados um a um (QA 12/10), sem
// descartar o que já estava escolhido.
export function validarArquivosComprovante(fileList, jaEscolhidos = 0) {
  const arquivos = Array.from(fileList || []);
  const validos = [];
  const erros = [];
  if (!arquivos.length) {
    erros.push("Nenhum arquivo selecionado.");
    return { validos, erros };
  }
  const espaco = Math.max(0, COMPROVANTE_MAX_ARQUIVOS - (Number(jaEscolhidos) || 0));
  const excedentes = [];
  arquivos.forEach((arquivo) => {
    const nome = (arquivo && arquivo.name) || "(sem nome)";
    const ext = extensaoDe(nome);
    if (COMPROVANTE_EXTENSOES.indexOf(ext) === -1) {
      erros.push(`Tipo não permitido: ${nome}. Aceitos: ${COMPROVANTE_EXTENSOES.join(", ")}.`);
      return;
    }
    if (!arquivo.size) {
      erros.push(`Arquivo vazio: ${nome}.`);
      return;
    }
    if (arquivo.size > COMPROVANTE_MAX_BYTES) {
      erros.push(`Arquivo acima de 10 MB: ${nome}.`);
      return;
    }
    if (validos.length >= espaco) {
      excedentes.push(nome);
      return;
    }
    validos.push(arquivo);
  });
  if (excedentes.length) {
    erros.push(`Limite de ${COMPROVANTE_MAX_ARQUIVOS} arquivos por envio: ${excedentes.join(", ")} não ${excedentes.length === 1 ? "foi adicionado" : "foram adicionados"}.`);
  }
  return { validos, erros };
}

export async function listarComprovantes(pedido) {
  const { data } = await api.get("/pedidos/comprovantes", { params: { pedido } });
  return data || { ocorrencias: [], manuais: [], storageDisponivel: false };
}

// 201 { enviados, ids }. O interceptor de api.js tira o Content-Type com FormData.
export async function enviarComprovantes({ pedido, idNotaFiscal, arquivos }) {
  const form = new FormData();
  form.append("pedido", pedido);
  if (idNotaFiscal) form.append("idNotaFiscal", String(idNotaFiscal));
  Array.from(arquivos || []).forEach((f) => form.append("arquivos", f, f.name));
  const { data } = await api.post("/pedidos/comprovantes", form);
  return data || { enviados: 0, ids: [] };
}

export async function removerComprovante(id) {
  const { data } = await api.delete(`/pedidos/comprovantes/${id}`);
  return data || { ok: true };
}

// ─── Troca de transportadora (H6) ───────────────────────────────────────────
// [{ id, cnpj, razaoSocial, fantasia }] com vínculo ativo com o embarcador.
export async function listarTransportadoras() {
  const { data } = await api.get("/pedidos/transportadoras");
  return lista(data);
}

export const nomeTransportadora = (t) =>
  (t && (t.fantasia || t.razaoSocial || t.nome)) || (t && t.id ? `#${t.id}` : "-");

// { notas[], de[], para }; 409 { code: 'troca_bloqueada', notas[] } | { code: 'mesma_transportadora' }.
export async function trocarTransportadora({ pedido, idTransportadora, motivo }) {
  const { data } = await api.post("/pedidos/transportadora", {
    pedido,
    idTransportadora: Number(idTransportadora),
    motivo,
  });
  return data;
}

// ─── Notas dos pedidos, NOTFIS e romaneio (H4) ──────────────────────────────
// [{ idNota, numero, serie, pedido, idTransportadora, transportadora, idRomaneio,
//    temCte, envios[], pedidoOperacional|null }]. `envios`/`pedidoOperacional`
// podem faltar em versões antigas do backend: normaliza para [] / null.
export async function notasDosPedidos(pedidos) {
  const { data } = await api.post("/pedidos/notas", { pedidos: lista(pedidos) });
  return lista(data).map((n) => ({
    ...n,
    envios: lista(n.envios),
    pedidoOperacional: n.pedidoOperacional || null,
  }));
}

// Transportadoras distintas de uma lista de notas: [{ id, nome }].
export function transportadorasDasNotas(notas) {
  const vistas = [];
  lista(notas).forEach((n) => {
    const id = n.idTransportadora;
    if (id === null || id === undefined) {
      if (!vistas.some((t) => t.id === null)) vistas.push({ id: null, nome: "Sem transportadora" });
      return;
    }
    if (!vistas.some((t) => String(t.id) === String(id))) vistas.push({ id, nome: n.transportadora || `#${id}` });
  });
  return vistas;
}

// Nome do arquivo do Content-Disposition (`attachment; filename="X"`).
export function nomeDoContentDisposition(header, fallback) {
  const h = String(header || "");
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(h);
  if (utf8 && utf8[1]) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch (_) {
      /* cai no filename simples */
    }
  }
  const simples = /filename="?([^";]+)"?/i.exec(h);
  return (simples && simples[1] && simples[1].trim()) || fallback;
}

// { blob, nomeArquivo }. 409 { error, transportadoras[] } quando mistura
// transportadoras: a tela valida antes com notasDosPedidos.
export async function gerarNotfis(pedidos) {
  const resp = await api.post("/pedidos/notfis", { pedidos: lista(pedidos) }, { responseType: "blob" });
  const headers = resp.headers || {};
  return {
    blob: resp.data,
    nomeArquivo: nomeDoContentDisposition(headers["content-disposition"], "NOTFIS.txt"),
  };
}

// ─── Etiquetas (H5, consome a PO1-3378) ─────────────────────────────────────
const paramsEmbarcador = (embarcadorId) => (embarcadorId ? { embarcadorId } : {});

// { envios, transportadoras, propostas, cotacao, sugestao|null, volumesSugeridos[] }
export async function opcoesEtiqueta(idNota, embarcadorId) {
  const { data } = await api.get(`/notas-fiscais/consulta/${idNota}/etiqueta`, { params: paramsEmbarcador(embarcadorId) });
  return data || {};
}

// { status, envios[], erros[], dryRun }
export async function gerarEtiqueta(idNota, corpo, embarcadorId) {
  const { data } = await api.post(`/notas-fiscais/consulta/${idNota}/etiqueta`, corpo, { params: paramsEmbarcador(embarcadorId) });
  return data || {};
}

// Lê o header X-Etiquetas-Ignoradas = encodeURIComponent(JSON [{ numero, motivo }]).
export function ignoradasDoHeader(valor) {
  if (!valor) return [];
  try {
    const parsed = JSON.parse(decodeURIComponent(String(valor)));
    return lista(parsed);
  } catch (_) {
    try {
      return lista(JSON.parse(String(valor)));
    } catch (__) {
      return [];
    }
  }
}

// { blob, nomeArquivo, ignoradas[] }. formato 'pdf' | 'zpl'.
export async function arquivoEtiquetas(pedidos, formato) {
  const resp = await api.post("/pedidos/etiquetas/arquivo", { pedidos: lista(pedidos), formato }, { responseType: "blob" });
  const headers = resp.headers || {};
  return {
    blob: resp.data,
    nomeArquivo: nomeDoContentDisposition(headers["content-disposition"], `etiquetas.${formato === "zpl" ? "zpl" : "pdf"}`),
    ignoradas: ignoradasDoHeader(headers["x-etiquetas-ignoradas"]),
  };
}

// ─── Lote de despacho (H3) ──────────────────────────────────────────────────
// { lotes:[{ id, codigo, transportadora, adicionados, fechadoPorVolume }], adicionados, ignorados[] }
export async function adicionarAoLote(pedidos) {
  const { data } = await api.post("/despacho/lotes/adicionar", { pedidos: lista(pedidos) });
  return data || { lotes: [], adicionados: 0, ignorados: [] };
}

// ─── Utilidades ─────────────────────────────────────────────────────────────
// Executa `fn(item, indice)` para cada item com no máximo `concorrencia` em
// paralelo; devolve os resultados na ordem dos itens (nunca rejeita: cada
// posição é { ok: true, valor } ou { ok: false, erro }).
export async function emParalelo(itens, concorrencia, fn) {
  const fila = lista(itens);
  const resultados = new Array(fila.length);
  let proximo = 0;
  const trabalhador = async () => {
    while (proximo < fila.length) {
      const i = proximo;
      proximo += 1;
      try {
        resultados[i] = { ok: true, valor: await fn(fila[i], i) };
      } catch (erro) {
        resultados[i] = { ok: false, erro };
      }
    }
  };
  const n = Math.max(1, Math.min(Number(concorrencia) || 1, fila.length || 1));
  const trabalhadores = [];
  for (let k = 0; k < n; k += 1) trabalhadores.push(trabalhador());
  await Promise.all(trabalhadores);
  return resultados;
}

// Resposta de erro quando o request pediu blob: o JSON de erro vem como Blob.
export async function lerErroBlob(erro) {
  const d = erro && erro.response && erro.response.data;
  if (d && typeof d.text === "function") {
    try {
      return JSON.parse(await d.text());
    } catch (_) {
      return null;
    }
  }
  return d && typeof d === "object" ? d : null;
}

// Mensagem padrão dos erros do módulo ({ error } no backend_hub; 503 storage).
export function mensagemErroPedido(erro, fallback) {
  const status = erro && erro.response && erro.response.status;
  const payload = erro && erro.response && erro.response.data;
  const doBackend =
    payload && typeof payload === "object"
      ? (typeof payload.error === "string" && payload.error) || (typeof payload.message === "string" && payload.message) || ""
      : typeof payload === "string"
        ? payload
        : "";
  if (status === 503) return "Armazenamento de anexos não configurado no servidor. Avise o suporte.";
  if (status === 413) return "Arquivo acima do limite de 10 MB.";
  if (status === 403) return doBackend || "Você não tem permissão para esta ação.";
  if (status === 404) return doBackend || "Pedido não encontrado neste embarcador.";
  return doBackend || (erro && erro.message) || fallback || "Não foi possível concluir a operação.";
}

// Erros por campo do 400 { errors: { campo: msg } }.
export function errosDeCampos(erro) {
  const payload = erro && erro.response && erro.response.data;
  const errors = payload && payload.errors;
  return errors && typeof errors === "object" ? errors : {};
}
